import { LayoutTemplateRegistry } from '@src/editor/models/layout/LayoutTemplateRegistry';
import {
  extractFitTypeFromTemplateId,
  FitType,
  mapFitTypeToTemplateId,
} from '@src/editor/models/layout/legacyLayoutMapping';
import { useRefreshActiveElement } from '@src/hooks/useRefreshActiveElement';
import useUpdateExistingScene from '@src/hooks/useUpdateExistingScene';
import { SceneService } from '@src/services/SceneService';
import { useEditor, useVideoCreatorStore } from '@src/stores-v2';

import { LogManager } from '../../utility/Logger';

import type { ElementState } from '@src/renderer/ElementState';

export const LAYOUT_TYPES = [
  FitType.Stacked1To1,
  FitType.Stacked1To3,
  FitType.BlurredBackground,
];
export const NON_LAYOUT_TYPES = [FitType.Cover, FitType.Contain, FitType.Fill];

const logger = LogManager.getLogger('hook:useLayoutAwareFitPropertyState');

type ReturnType = {
  hasMixedOptions: boolean;
  updateFitProperty: (
    newValue: string | null,
    elementId: string,
  ) => Promise<void>;
  determineValueForStackedLayout: (
    elementId: string,
    initialValue?: string,
  ) => string | undefined;
  handleStackedLayoutFitChange: (value: string | null) => Promise<void>;
};

export default function useLayoutAwareFitPropertyState({
  options,
  propertyName,
  defaultValue,
  activeElement,
}: {
  options: Array<{ caption: string; value: any }>;
  propertyName: string;
  defaultValue: any;
  activeElement: ElementState;
}): ReturnType {
  const videoCreator = useVideoCreatorStore();
  const videoSourceStore = videoCreator.rootStore.videoSourceStore;
  const editorStore = useEditor();
  const sceneLayoutIndexStore = videoCreator.rootStore.sceneLayoutIndexStore;

  const { updateExistingScene } = useUpdateExistingScene();

  // Resolve an element ID to its visible representation ID.
  // When the real/parent element is selected, SceneLayoutIndexStore indexes it
  // with role 'parent', not 'representation'. This helper maps it to the
  // representation element so layout index lookups work correctly.
  const resolveToRepresentationId = (elementId: string): string => {
    return (
      sceneLayoutIndexStore.resolveVisibleRepresentationId(
        elementId,
        videoCreator.time,
      ) ?? elementId
    );
  };

  // Check if we're dealing with a dropdown that has both regular fit and layout options
  const hasMixedOptions =
    options.some(option => LAYOUT_TYPES.includes(option.value)) &&
    options.some(option => NON_LAYOUT_TYPES.includes(option.value));

  const handleSingleElementPropertiesUpdate = async (
    propertiesToUpdate: Record<string, any>,
    elementId: string,
  ) => {
    logger.debug('handleFitPropertyUpdate called', {
      propertiesToUpdate,
      elementId,
    });

    if (elementId) {
      // Use headless editor UpdateElementCommand
      logger.debug('Attempting headless editor fit update');
      const success = editorStore.editor.commandUpdateElement(
        elementId,
        propertiesToUpdate,
      );

      if (!success) {
        logger.warn(
          `Failed to update fit property for element ${elementId}:`,
          propertiesToUpdate,
        );
      } else {
        logger.debug('Headless editor fit update successful');
      }
    }
  };

  const updateFitProperty = async (
    newValue: string | null,
    elementId: string,
  ): Promise<void> => {
    await handleSingleElementPropertiesUpdate(
      {
        // only single property is being updated, no dimension adjustments
        [propertyName]: newValue,
      },
      elementId,
    );
  };

  const determineValueForStackedLayout = (
    elementId: string,
    initialValue: any,
  ): string | undefined => {
    // 1. Try SceneLayoutIndexStore (handles layout-generated elements
    //    whose IDs only exist in the renderer, not on the headless timeline).
    //    Resolve the element ID so parent elements are mapped to their
    //    representation counterpart.
    const resolvedId = resolveToRepresentationId(elementId);
    const record = sceneLayoutIndexStore.getRepresentationRoleRecordFor(
      resolvedId,
      videoCreator.time,
    );
    if (record?.link?.sceneId) {
      const scene = editorStore.editor.timeline.findSceneById(
        record.link.sceneId,
      );
      if (scene) {
        return extractFitTypeFromTemplateId(scene.layout.layoutTemplateId);
      }
    }

    // 2. Fallback: SceneService path (for elements that live on the headless timeline)
    const sceneService = new SceneService();
    const templateId = sceneService.getLayoutTypeForElement(
      editorStore.editor.timeline,
      elementId,
      videoCreator.time,
    );
    if (templateId) {
      return extractFitTypeFromTemplateId(templateId);
    }

    return initialValue || defaultValue;
  };

  const refreshActiveElement = useRefreshActiveElement();

  const handleStackedLayoutFitChange = async (value: string | null) => {
    const elementId = activeElement.source.id;
    const resolvedId = resolveToRepresentationId(elementId);

    // important to capture the base element before changing the layout
    const baseElement = videoCreator.getRealActiveElement() ?? activeElement;

    // Check if we're dealing with a child video element inside a composition
    const isInnerVideo = isInnerVideoElement(
      elementId,
      activeElement.source.type,
      videoSourceStore.source,
    );

    // For inner video elements, check if they're layout-generated.
    // Layout-generated elements don't exist on the headless timeline,
    // so updateFitProperty would fail — fall through to scene update path instead.
    if (isInnerVideo) {
      const innerRecord = sceneLayoutIndexStore.getRepresentationRoleRecordFor(
        resolvedId,
        videoCreator.time,
      );
      if (!innerRecord?.link?.sceneId) {
        // Not a layout-generated element — update fit property directly
        await updateFitProperty(value, elementId);
        return;
      }
      // Layout-generated inner video — fall through to scene update path
    }

    // Cover/Contain/Fill are element-level fit. For a standalone video element
    // (not bound to a stacked-layout scene), write fit directly — the scene/template
    // path below only applies to layout compositions and would no-op here.
    if (!isInnerVideo && NON_LAYOUT_TYPES.includes(value as FitType)) {
      const standaloneRecord =
        sceneLayoutIndexStore.getRepresentationRoleRecordFor(
          resolvedId,
          videoCreator.time,
        );
      if (!standaloneRecord?.link?.sceneId) {
        await updateFitProperty(value, elementId);
        return;
      }
    }

    if (!videoCreator.currentVideo) {
      throw new Error(
        'Cannot handle stacked layout fit change: currentVideo not set',
      );
    }

    // Map the new fit/layout value to a template ID
    const newTemplateId = mapFitTypeToTemplateId(value as string);

    const template =
      LayoutTemplateRegistry.getInstance().getTemplate(newTemplateId);
    if (!template) {
      logger.warn(`Template ${newTemplateId} not found`);
      return;
    }

    // Find scene: try layout index first, then SceneService fallback
    const record = sceneLayoutIndexStore.getRepresentationRoleRecordFor(
      resolvedId,
      videoCreator.time,
    );
    let bestMatchingScene;
    if (record?.link?.sceneId) {
      bestMatchingScene = editorStore.editor.timeline.findSceneById(
        record.link.sceneId,
      );
    }
    if (!bestMatchingScene) {
      const sceneService = new SceneService();
      bestMatchingScene = sceneService.findSceneForElement(
        editorStore.editor.timeline,
        elementId,
        videoCreator.time,
      );
    }

    let layoutChanged;
    if (bestMatchingScene) {
      const elementSource = videoCreator.getElementSource(activeElement);
      layoutChanged = await updateExistingScene(
        bestMatchingScene,
        newTemplateId,
        elementSource,
      );
    }

    if (layoutChanged) {
      await refreshActiveElement(baseElement);
    }
  };

  return {
    hasMixedOptions,
    updateFitProperty,
    determineValueForStackedLayout,
    handleStackedLayoutFitChange,
  };
}

export function isInnerVideoElement(
  elementId: string,
  elementType: string,
  source: Record<string, any>,
): boolean {
  if (elementType !== 'video' || !source.elements) {
    return false;
  }

  return source.elements.some((el: any) =>
    isCompositionWithElement(el, elementId),
  );
}

function isCompositionWithElement(el: any, elementId: string) {
  return (
    el.type === 'composition' &&
    el.elements &&
    Array.isArray(el.elements) &&
    el.elements.some((child: any) => child.id === elementId)
  );
}
